/**
 * EventJourney — Prashasta Split-Panel Landing Page
 *
 * Design: matches build-replica-5 reference
 * Left panel  — Prashasta marketing website preview
 * Right panel — Smart Event Studio (step 1: event type selection)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "@/contexts/AuthContext";
import {
  MapPin, UtensilsCrossed, Sparkles, Camera, Music, Users,
  Heart, Gem, Gift, Briefcase, MonitorPlay, Zap,
  ChevronRight, ArrowRight, Check, Phone, Mail,
  Diamond, Star, Calendar, Handshake, LogIn, LayoutDashboard,
} from 'lucide-react';

// ── Images ───────────────────────────────────────────────────────────────────
const HERO_LEFT =
  'https://images.unsplash.com/photo-1519741497674-611481863552?w=1400&h=900&fit=crop&q=85';

const EVENT_TYPES = [
  {
    id: 'WEDDING',
    label: 'Wedding & Celebrations',
    Icon: Heart,
    img: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=500&h=340&fit=crop&q=75',
  },
  {
    id: 'ENGAGEMENT',
    label: 'Engagement',
    Icon: Gem,
    img: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=500&h=340&fit=crop&q=75',
  },
  {
    id: 'BIRTHDAY',
    label: 'Birthday',
    Icon: Gift,
    img: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=500&h=340&fit=crop&q=75',
  },
  {
    id: 'CORPORATE',
    label: 'Corporate Event',
    Icon: Briefcase,
    img: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=500&h=340&fit=crop&q=75',
  },
  {
    id: 'CONFERENCE',
    label: 'Conference',
    Icon: MonitorPlay,
    img: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=500&h=340&fit=crop&q=75',
  },
  {
    id: 'PRODUCT_LAUNCH',
    label: 'Product Launch',
    Icon: Zap,
    img: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=500&h=340&fit=crop&q=75',
  },
];

const SERVICES = [
  { Icon: MapPin, label: 'Venues' },
  { Icon: UtensilsCrossed, label: 'Catering' },
  { Icon: Sparkles, label: 'Décor & Styling' },
  { Icon: Camera, label: 'Photography' },
  { Icon: Music, label: 'Entertainment' },
  { Icon: Users, label: 'Guest Hospitality' },
];

const BENEFITS = [
  { Icon: Diamond, label: 'Premium Experiences' },
  { Icon: Handshake, label: 'Trusted Partners' },
  { Icon: Calendar, label: 'End to End Event Management' },
  { Icon: Star, label: 'Personalised Plans' },
];

const NAV_LINKS = ['Weddings', 'Corporate', 'Social Events', 'Our Work', 'Why Prashasta'];

const PROCESS_STEPS = [
  { num: 2, title: 'Personalise', desc: 'Tell us more about your celebration' },
  { num: 3, title: 'Configure', desc: 'Choose services and experience level' },
  { num: 4, title: 'Review', desc: 'See an indicative plan and estimate' },
  { num: 5, title: 'Connect', desc: 'Share your details with our team' },
];

// ── Estimate helper ───────────────────────────────────────────────────────────
const BASE_RATES = {
  WEDDING: 5070, ENGAGEMENT: 3200, BIRTHDAY: 1900,
  CORPORATE: 2800, CONFERENCE: 1800, PRODUCT_LAUNCH: 2600,
};
function indicativeEstimate(typeId, guests = 250) {
  const rate = BASE_RATES[typeId] || 2500;
  const raw = rate * guests;
  return Math.ceil(raw / 10000) * 10000;
}
function fmtINR(paise) {
  const rupees = paise / 100;
  if (rupees >= 10000000) return `₹${(rupees / 10000000).toFixed(2)} Cr`;
  if (rupees >= 100000)   return `₹${(rupees / 100000).toFixed(2)} L`;
  return `₹${rupees.toLocaleString('en-IN')}`;
}
// estimate in rupees (minor units divided by 100)
function estimate(typeId) {
  const rate = BASE_RATES[typeId] || 2500;
  const rawRupees = rate * 250; // 250 guests default
  return Math.ceil(rawRupees / 10000) * 10000;
}
function fmtRupees(rupees) {
  if (rupees >= 10000000) return `₹${(rupees / 10000000).toFixed(0)} Cr`;
  if (rupees >= 100000) {
    const l = rupees / 100000;
    return `₹${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} L`;
  }
  return `₹${rupees.toLocaleString('en-IN')}`;
}

// ── Styles ───────────────────────────────────────────────────────────────────
const EJ_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

.ej2-root { font-family:'DM Sans',sans-serif; background:#F5EEE6; min-height:100vh; }

.ej2-heading { font-family:'Cormorant Garamond',serif; }

/* header */
.ej2-header {
  position:sticky; top:0; z-index:50;
  background:rgba(250,247,242,0.97);
  backdrop-filter:blur(16px);
  -webkit-backdrop-filter:blur(16px);
  border-bottom:1px solid rgba(201,161,95,0.18);
  box-shadow:0 2px 16px -4px rgba(32,27,25,0.07);
}

/* cards */
.ej2-panel {
  background:#fff;
  border-radius:14px;
  overflow:hidden;
  box-shadow:0 8px 40px -12px rgba(32,27,25,0.18);
  border:1px solid rgba(201,161,95,0.12);
}

/* left mini-nav */
.ej2-mini-nav {
  background:#fff;
  border-bottom:1px solid rgba(32,27,25,0.08);
  padding:0 20px;
  height:44px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  flex-shrink:0;
}

/* event type card */
.ej2-et-card {
  border-radius:10px;
  overflow:hidden;
  cursor:pointer;
  border:2px solid transparent;
  transition:transform 0.22s cubic-bezier(0.22,1,0.36,1),
             border-color 0.18s,
             box-shadow 0.22s;
  position:relative;
}
.ej2-et-card:hover { transform:translateY(-2px); box-shadow:0 8px 28px -6px rgba(32,27,25,0.18) !important; }
.ej2-et-card[data-sel=true] { border-color:#C9A15F; box-shadow:0 0 0 3px rgba(201,161,95,0.18) !important; }

.ej2-et-img { width:100%; height:90px; object-fit:cover; display:block; }

/* service strip */
.ej2-svc-pill {
  display:flex; flex-direction:column; align-items:center; gap:4px;
  padding:10px 8px 8px;
  cursor:pointer;
  flex:1;
  transition:background 0.18s;
  border-right:1px solid rgba(32,27,25,0.07);
}
.ej2-svc-pill:last-child { border-right:none; }
.ej2-svc-pill:hover { background:#FAF7F2; }
.ej2-svc-icon { color:#C9A15F; transition:transform 0.2s; }
.ej2-svc-pill:hover .ej2-svc-icon { transform:translateY(-2px); }

/* gold btn */
.ej2-btn-gold {
  background:#C9A15F; color:#1C1917; border:none; cursor:pointer;
  font-family:'DM Sans',sans-serif; font-weight:600;
  transition:background 0.18s, transform 0.15s;
}
.ej2-btn-gold:hover { background:#b8903e; transform:translateY(-1px); }

/* outline btn */
.ej2-btn-outline {
  background:transparent; cursor:pointer;
  font-family:'DM Sans',sans-serif; font-weight:500;
  border:1px solid rgba(32,27,25,0.2); color:#57534E;
  transition:background 0.18s;
}
.ej2-btn-outline:hover { background:#FAF7F2; }

/* plan summary */
.ej2-plan-card {
  background:#1C1917;
  border-radius:12px;
  color:#FAF7F2;
  flex-shrink:0;
}

/* step dots at top */
.ej2-step { transition:all 0.25s; }
.ej2-step-active { background:#C9A15F !important; color:#fff !important; }
.ej2-step-done   { background:#C9A15F !important; color:#fff !important; }

/* hero text */
.ej2-hero-tag {
  font-size:10px; letter-spacing:0.2em; text-transform:uppercase;
  color:rgba(250,247,242,0.75); font-family:'DM Sans',sans-serif;
}

/* bottom steps */
.ej2-step-item {
  display:flex; align-items:flex-start; gap:12px;
  padding:20px 28px;
  border-right:1px solid rgba(201,161,95,0.15);
}
.ej2-step-item:last-child { border-right:none; }

/* benefit icon */
.ej2-benefit {
  display:flex; align-items:center; gap:6px;
  padding:0 16px;
  border-right:1px solid rgba(201,161,95,0.25);
}
.ej2-benefit:last-child { border-right:none; }

/* Staff Login / Open Workspace button */
.ej2-staff-btn {
  display:inline-flex; align-items:center; gap:7px;
  padding:8px 18px; border-radius:24px;
  font-family:'DM Sans',sans-serif; font-size:13px;
  font-weight:600; letter-spacing:0.02em;
  cursor:pointer; text-decoration:none;
  transition:all 0.2s; white-space:nowrap; flex-shrink:0;
  border:1.5px solid transparent;
}
.ej2-staff-btn-login {
  background:rgba(250,247,242,0.9);
  color:#C9A15F;
  border-color:rgba(201,161,95,0.55);
}
.ej2-staff-btn-login:hover {
  background:#C9A15F; color:#fff; border-color:#C9A15F;
}
.ej2-staff-btn-workspace {
  background:linear-gradient(135deg,#C9A15F,#B8924E);
  color:#fff; border-color:#C9A15F;
  box-shadow:0 2px 10px rgba(201,161,95,0.35);
}
.ej2-staff-btn-workspace:hover {
  background:linear-gradient(135deg,#B8924E,#A07840);
  box-shadow:0 4px 16px rgba(201,161,95,0.45);
}
@media (max-width:520px) {
  .ej2-staff-btn { padding:7px 12px; }
  .ej2-staff-btn-label { display:none; }
}

/* scrollbar hidden for left panel hero */
.ej2-left-scroll { overflow-y:auto; scrollbar-width:none; }
.ej2-left-scroll::-webkit-scrollbar { display:none; }

@keyframes ej2FadeUp {
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
}
.ej2-fadein { animation:ej2FadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both; }
.ej2-d1 { animation-delay:0.06s; }
.ej2-d2 { animation-delay:0.12s; }
.ej2-d3 { animation-delay:0.18s; }
.ej2-d4 { animation-delay:0.26s; }

@media (max-width:900px) {
  .ej2-benefit { display:none !important; }
}
@media (max-width:768px) {
  .ej2-main-grid { grid-template-columns:1fr !important; }
  .ej2-mini-nav-links { display:none !important; }
  .ej2-et-grid { grid-template-columns:repeat(2,1fr) !important; }
  .ej2-right-inner { grid-template-columns:1fr !important; }
  .ej2-plan-sidebar { display:none !important; }
  .ej2-steps-grid { grid-template-columns:1fr 1fr !important; }
}
`;

// ── Component ─────────────────────────────────────────────────────────────────
export default function EventJourney() {
  const navigate = useNavigate();
  const { user, uatConfig } = useAuth();
  const [selected, setSelected] = useState(null);

  const selectedItem = EVENT_TYPES.find(e => e.id === selected);
  const est = selected ? estimate(selected) : null;

  // Canonical login path — follows same logic as App.js RedirectToLogin.
  // UAT role picker only shown in UAT environment (uatConfig?.enabled).
  // Never exposed to public portal visitors in production.
  const loginPath = uatConfig?.enabled ? "/uat-login" : "/login";

  const handleNext = () => {
    const params = selected ? `?event_type=${selected}` : '';
    navigate(`/smart-studio${params}`);
  };

  return (
    <div className="ej2-root">
      <style>{EJ_CSS}</style>

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <header className="ej2-header" data-testid="ej2-header">
        <div style={{ maxWidth: 1380, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: 64, gap: 16 }}>

            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <img
                src="/prashasta-logo.png"
                alt="Prashasta Events"
                style={{ height: 40, width: 'auto', objectFit: 'contain' }}
                data-testid="ej2-logo"
              />
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 36, background: 'rgba(201,161,95,0.3)', flexShrink: 0 }} />

            {/* Smart Event Studio title */}
            <div style={{ flex: 1 }}>
              <p className="ej2-heading" style={{ fontSize: 20, fontWeight: 600, color: '#1C1917', lineHeight: 1.2, margin: 0 }}>
                Smart Event Studio
              </p>
              <p style={{ fontSize: 12, color: '#78716C', margin: 0, marginTop: 2 }}>
                Your celebration, thoughtfully designed.
              </p>
            </div>

            {/* Benefits strip — hidden below 900px */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {BENEFITS.map(({ Icon, label }) => (
                <div key={label} className="ej2-benefit">
                  <div style={{ width: 28, height: 28, background: '#FAF7F2', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(201,161,95,0.25)' }}>
                    <Icon style={{ width: 13, height: 13, color: '#C9A15F' }} />
                  </div>
                  <span style={{ fontSize: 11, color: '#57534E', fontWeight: 500, maxWidth: 72, lineHeight: 1.3 }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Staff Login / Open Workspace — always visible */}
            {user ? (
              <a
                href="/home"
                className="ej2-staff-btn ej2-staff-btn-workspace"
                data-testid="open-workspace-btn"
                title="Open Prashasta CRM"
              >
                <LayoutDashboard style={{ width: 14, height: 14, flexShrink: 0 }} />
                <span className="ej2-staff-btn-label">Open Workspace</span>
              </a>
            ) : (
              <a
                href={loginPath}
                className="ej2-staff-btn ej2-staff-btn-login"
                data-testid="staff-login-btn"
                title="Open Workspace"
              >
                <LogIn style={{ width: 14, height: 14, flexShrink: 0 }} />
                <span className="ej2-staff-btn-label">Open Workspace</span>
              </a>
            )}

          </div>
        </div>
      </header>

      {/* ── MAIN SPLIT ──────────────────────────────────────────────── */}
      <main className="ej2-main-grid" style={{ maxWidth: 1380, margin: '0 auto', padding: '28px 24px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* LEFT PANEL — marketing website preview */}
        <div className="ej2-panel ej2-fadein" data-testid="ej2-left-panel" style={{ position: 'sticky', top: 80 }}>

          {/* Mini nav */}
          <div className="ej2-mini-nav">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <img src="/prashasta-logo-cropped.png" alt="" style={{ height: 26, width: 'auto', objectFit: 'contain' }} />
              <span className="ej2-heading" style={{ fontSize: 13, fontWeight: 700, color: '#1C1917', letterSpacing: '0.04em' }}>
                Prashasta <span style={{ fontWeight: 400, color: '#78716C', fontSize: 11 }}>EVENTS</span>
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              {NAV_LINKS.map(link => (
                <span key={link} className="ej2-mini-nav-links" style={{ fontSize: 11, color: '#57534E', cursor: 'pointer', fontWeight: 500, letterSpacing: '0.04em' }}>
                  {link.toUpperCase()}
                </span>
              ))}
              <button
                className="ej2-btn-gold"
                onClick={handleNext}
                style={{ padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}
                data-testid="ej2-plan-your-event-btn"
              >
                PLAN YOUR EVENT
              </button>
            </div>
          </div>

          {/* Hero image + overlay */}
          <div style={{ position: 'relative', height: 320, overflow: 'hidden' }}>
            <img
              src={HERO_LEFT}
              alt="Luxury wedding venue"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%', display: 'block' }}
            />
            {/* dark overlay */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(28,25,23,0.45) 0%, rgba(28,25,23,0.55) 60%, rgba(28,25,23,0.7) 100%)' }} />

            {/* Text overlay */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '24px 28px' }}>
              <p className="ej2-hero-tag" style={{ marginBottom: 12 }}>Since 2017 · Hyderabad and beyond</p>
              <h1 className="ej2-heading" style={{ fontSize: 36, fontWeight: 300, color: '#FAF7F2', lineHeight: 1.15, margin: 0 }}>
                Let's create <em style={{ fontStyle: 'italic' }}>something<br />extraordinary.</em>
              </h1>
              <p style={{ fontSize: 13, color: 'rgba(250,247,242,0.8)', marginTop: 8, marginBottom: 16 }}>
                Luxury events. Thoughtfully planned.
              </p>
              <button
                className="ej2-btn-gold"
                onClick={handleNext}
                style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 8, fontSize: 13 }}
                data-testid="ej2-start-planning-btn"
              >
                Start planning <ArrowRight style={{ width: 14, height: 14 }} />
              </button>
            </div>
          </div>

          {/* Service categories strip */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(32,27,25,0.07)', background: '#fff' }}>
            {SERVICES.map(({ Icon, label }) => (
              <button
                key={label}
                className="ej2-svc-pill"
                onClick={handleNext}
                style={{ border: 'none', background: 'transparent' }}
                data-testid={`ej2-svc-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="ej2-svc-icon" style={{ width: 18, height: 18 }} />
                <span style={{ fontSize: 11, color: '#57534E', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>
              </button>
            ))}
          </div>

          {/* Feature cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid rgba(32,27,25,0.06)' }}>
            <div style={{ padding: '20px 22px', borderRight: '1px solid rgba(32,27,25,0.07)' }}>
              <h3 className="ej2-heading" style={{ fontSize: 16, fontWeight: 600, color: '#1C1917', margin: '0 0 6px' }}>
                One team, thoughtful details
              </h3>
              <p style={{ fontSize: 12, color: '#78716C', lineHeight: 1.5, margin: '0 0 12px' }}>
                Venue, décor, catering and coordination brought together around your celebration.
              </p>
              <button
                onClick={handleNext}
                style={{ fontSize: 12, color: '#C9A15F', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontFamily: "'DM Sans',sans-serif" }}
              >
                EXPLORE SERVICES <ArrowRight style={{ width: 12, height: 12 }} />
              </button>
            </div>
            <div style={{ padding: '20px 22px' }}>
              <h3 className="ej2-heading" style={{ fontSize: 16, fontWeight: 600, color: '#1C1917', margin: '0 0 6px' }}>
                From your first idea
              </h3>
              <p style={{ fontSize: 12, color: '#78716C', lineHeight: 1.5, margin: '0 0 12px' }}>
                Tell us what you have in mind. We'll help shape a plan around your people and priorities.
              </p>
              <button
                onClick={handleNext}
                style={{ fontSize: 12, color: '#C9A15F', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontFamily: "'DM Sans',sans-serif" }}
              >
                BUILD YOUR EVENT PLAN <ArrowRight style={{ width: 12, height: 12 }} />
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL — Smart Event Studio */}
        <div className="ej2-panel ej2-fadein ej2-d2" data-testid="ej2-right-panel">

          {/* Studio header row */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(32,27,25,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/prashasta-logo-cropped.png" alt="" style={{ height: 24, width: 'auto' }} />
              <span className="ej2-heading" style={{ fontSize: 13, fontWeight: 700, color: '#1C1917' }}>Prashasta <span style={{ fontWeight: 400, color: '#78716C', fontSize: 11 }}>EVENTS</span></span>
            </div>
            {/* Step dots */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <div
                  key={n}
                  className={`ej2-step ${n === 1 ? 'ej2-step-active' : ''}`}
                  style={{ width: n === 1 ? 28 : 22, height: n === 1 ? 28 : 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: n === 1 ? '#C9A15F' : '#F0EBE4', color: n === 1 ? '#fff' : '#78716C', transition: 'all 0.2s' }}
                >
                  {n}
                </div>
              ))}
            </div>
          </div>

          {/* Studio form area */}
          <div className="ej2-right-inner" style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 0 }}>

            {/* Event type grid */}
            <div style={{ padding: '20px 20px 0 20px', borderRight: '1px solid rgba(32,27,25,0.07)' }}>
              <h2 className="ej2-heading" style={{ fontSize: 22, fontWeight: 600, color: '#1C1917', margin: '0 0 4px' }}>
                What are you planning?
              </h2>
              <p style={{ fontSize: 12, color: '#78716C', margin: '0 0 16px' }}>
                Choose an event type to begin shaping your plan.
              </p>

              {/* Grid 3×2 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }} className="ej2-et-grid">
                {EVENT_TYPES.map(({ id, label, Icon, img }) => {
                  const isSel = selected === id;
                  return (
                    <div
                      key={id}
                      className="ej2-et-card"
                      data-sel={isSel}
                      data-testid={`ej2-event-type-${id.toLowerCase()}`}
                      onClick={() => setSelected(id)}
                      style={{ boxShadow: isSel ? 'none' : '0 2px 10px -4px rgba(32,27,25,0.12)' }}
                    >
                      <img src={img} alt={label} className="ej2-et-img" />
                      <div style={{ padding: '7px 8px 8px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#1C1917' }}>{label}</span>
                        {isSel && <Check style={{ width: 12, height: 12, color: '#C9A15F', flexShrink: 0 }} />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action row */}
              <div style={{ display: 'flex', gap: 10, padding: '14px 0', borderTop: '1px solid rgba(32,27,25,0.07)' }}>
                <button
                  className="ej2-btn-outline"
                  style={{ flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 12 }}
                  onClick={() => setSelected(null)}
                >
                  ← Back
                </button>
                <button
                  className="ej2-btn-gold"
                  disabled={!selected}
                  onClick={handleNext}
                  style={{ flex: 2, padding: '9px 12px', borderRadius: 8, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: selected ? 1 : 0.5, cursor: selected ? 'pointer' : 'not-allowed' }}
                  data-testid="ej2-next-btn"
                >
                  Next: Personalise <ChevronRight style={{ width: 13, height: 13 }} />
                </button>
              </div>
            </div>

            {/* Your Event Plan summary */}
            <div className="ej2-plan-sidebar" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="ej2-plan-card" style={{ padding: 14 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(250,247,242,0.55)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 10px' }}>
                  Your Event Plan
                </p>

                <PlanRow Icon={Heart} label="Event type" value={selectedItem?.label || 'Not selected'} />
                <PlanRow Icon={MapPin} label="Location" value="Hyderabad" />
                <PlanRow Icon={Calendar} label="Event date" value="Not selected" dim />
                <PlanRow Icon={Users} label="Expected guests" value="250" />
                <PlanRow Icon={Star} label="Experience" value="Premium" />

                {/* Estimate */}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(250,247,242,0.1)' }}>
                  <p style={{ fontSize: 10, color: 'rgba(250,247,242,0.45)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Indicative estimate
                  </p>
                  <p className="ej2-heading" style={{ fontSize: 22, fontWeight: 600, color: '#FAF7F2', margin: 0 }}>
                    {est ? fmtRupees(est) : '—'}
                  </p>
                </div>
              </div>

              {/* Next CTA */}
              <button
                className="ej2-btn-gold"
                disabled={!selected}
                onClick={handleNext}
                style={{ width: '100%', padding: '10px 0', borderRadius: 8, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}
                data-testid="ej2-plan-next-btn"
              >
                Next: Personalise <ChevronRight style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* ── BOTTOM PROCESS STEPS ────────────────────────────────────── */}
      <section style={{ maxWidth: 1380, margin: '24px auto 0', padding: '0 24px' }}>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(201,161,95,0.12)', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', boxShadow: '0 4px 20px -8px rgba(32,27,25,0.10)' }} className="ej2-steps-grid">
          {PROCESS_STEPS.map(({ num, title, desc }) => (
            <div key={num} className="ej2-step-item">
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#F5EEE6', border: '2px solid #C9A15F', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#C9A15F' }}>{num}</span>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#1C1917', margin: '0 0 3px' }}>{title}</p>
                <p style={{ fontSize: 11, color: '#78716C', margin: 0, lineHeight: 1.4 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────── */}
      <footer style={{ maxWidth: 1380, margin: '20px auto 0', padding: '16px 24px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: 11, color: '#A8A29E', margin: 0 }}>
          © 2026 Prashasta Events · Hyderabad · Visakhapatnam · Vijayawada
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={{ fontSize: 11, color: '#A8A29E', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Phone style={{ width: 11, height: 11 }} />
            +91 90009 93774
          </span>
          <span style={{ fontSize: 11, color: '#A8A29E', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Mail style={{ width: 11, height: 11 }} />
            info@prashastaevents.com
          </span>
        </div>
      </footer>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PlanRow({ Icon, label, value, dim }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{ width: 22, height: 22, background: 'rgba(201,161,95,0.15)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon style={{ width: 11, height: 11, color: '#C9A15F' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 9, color: 'rgba(250,247,242,0.45)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
        <p style={{ fontSize: 11, fontWeight: 600, color: dim ? 'rgba(250,247,242,0.4)' : '#FAF7F2', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</p>
      </div>
    </div>
  );
}
